import { useQuery } from '@apollo/client';
import { CKBox } from '@clearkit/react';
import { useState } from 'react';

import { SALESFORCE_RESOURCE_TYPE } from '~/components/SearchBuilder/utils/constants';
import SalesforceLogo from '~/images/destinations/Salesforce.svg';
import { destinationTypeSalesforceCreateAccount } from '~/lib/destinations';
import { readableSingularSegmentType } from '~/lib/segments';
import Disconnected from '~/pages/destinations/components/Disconnected';
import FormHeader from '~/pages/destinations/components/FormHeader';
import TriggerType from '~/pages/destinations/components/TriggerType';

import TraitConfiguration from '../components/TraitConfiguration';
import query from './query.graphql';
import './styles.css';

function CreateExternalRecordsAccountSalesforce({
  errors,
  refetch,
  accountConnectors,
  destination,
  _destinationOptions,
  segment,
  FormFooter,
}) {
  const accountConnector = accountConnectors.salesforce;
  const isNew = !destination;
  const initialConfig = isNew ? {} : destination.config;
  const humanizedSegmentType = readableSingularSegmentType(segment.type);
  const nameTraits = ['Company Name', 'Company Domain'];
  const mappedTraitLabels = {
    OwnerId: 'Account Owner',
    Type: 'Account Type',
  };

  const initialState = {
    traits: initialConfig.traits || [],
    triggerType: isNew ? 'segment_enter' : destination.triggerType,
  };

  const [traits, setTraits] = useState(initialState.traits);
  const [triggerType, setTriggerType] = useState(initialState.triggerType);
  const { data, loading } = useQuery(query);

  if (!accountConnector || accountConnector?.status !== 'active') {
    return (
      <Disconnected
        service="salesforce"
        onSuccess={refetch}
        Logo={SalesforceLogo}
        FormFooter={FormFooter}
      />
    );
  }

  if (loading) {
    return null;
  }

  return (
    <>
      <FormHeader
        customTitle={'Create new accounts in Salesforce'}
        destinationType={destinationTypeSalesforceCreateAccount}
        destinationName="Salesforce"
        destinationFlowDescription={'Configuration Destination'}
        segmentName={segment.name}
        segmentCollection={segment.collection.name}
      />

      <>
        <CKBox
          className={'py-3 flex flex-1 rounded-none'}
          style={{ background: '#fafbfc' }}>
          <div className={'mx-6'}>
            <div className={'flex basis-1 items-start shrink-1 mr-5 flex-col'}>
              <span
                className={'text-base font-medium shrink-0'}
                style={{ color: '#27282a' }}>
                Trigger
              </span>
              <span
                className={
                  'text-ellipsis whitespace-nowrap overflow-hidden max-w-full text-sm'
                }
                style={{ color: '#596168', marginTop: '4px' }}>
                We will only create a new account if the website domain doesn't
                exist in your Salesforce.{' '}
                <a
                  href="https://help.clearbit.com/hc/en-us/articles/360023453694"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'underline' }}>
                  Learn more
                </a>
              </span>
            </div>

            <TriggerType
              className={'external-account-trigger-type'}
              errors={errors}
              handleChange={(_, val) => setTriggerType(val)}
              isDisabled
              triggerType={triggerType}
              triggerTypes={[
                {
                  value: 'segment_enter',
                  label: (
                    <span style={{ color: '#596168' }}>
                      When a {humanizedSegmentType} <strong>enters</strong> the
                      audience
                    </span>
                  ),
                },
              ]}
            />
          </div>

          <TraitConfiguration
            salesforceResourceType={SALESFORCE_RESOURCE_TYPE.ACCOUNT}
            nameTraits={nameTraits}
            companyAttributes={data.companyAttributes}
            salesforceTraits={data.salesforceTraits}
            errors={errors}
            mappedTraitLabels={mappedTraitLabels}
            traits={traits}
            setTraits={setTraits}
            unsettableValues={[
              {
                trait: 'Website',
                value: 'Company Domain',
              },
            ]}
          />
        </CKBox>
        <FormFooter
          destinationConfig={{
            config: {
              traits,
            },
            triggerType,
          }}
        />
      </>
    </>
  );
}

CreateExternalRecordsAccountSalesforce.propTypes = {
  destination: PropTypes.object,
  destinationOptions: PropTypes.object,
  errors: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired,
  accountConnectors: PropTypes.shape({
    salesforce: PropTypes.object.isRequired,
  }),
  segment: PropTypes.object,
};

export default CreateExternalRecordsAccountSalesforce;
